import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { IGetPromotionsParams } from './apis';
import { featureName } from './constants';
import {
  ICashDiscountIncentiveData,
  IFeeCreditCardData,
  ILoyaltyProgramItem,
  IPromotionDetail,
  IPromotionList,
  IReferralConfig,
  ISettingTaxData,
  IUpdateReferralConfig,
} from './types';
import { ApiStatus } from 'services/commonTypes';

// Types
type SliceState = {
  tax: {
    status: ApiStatus;
    error?: string;
    data?: ISettingTaxData;
  };
  discount: {
    status: ApiStatus;
    error?: string;
    data?: ICashDiscountIncentiveData;
  };
  feeCreditCard: {
    status: ApiStatus;
    error?: string;
    data?: IFeeCreditCardData;
  };
  loyalty: {
    status: ApiStatus;
    error?: string;
    data?: ILoyaltyProgramItem[];
  };
  promotionList: {
    status: ApiStatus;
    error?: string;
    data?: IPromotionList;
  };
  promotionDetail: {
    status: ApiStatus;
    error?: string;
    data?: IPromotionDetail;
  };
  referral: {
    status: ApiStatus;
    error?: string;
    data?: IReferralConfig[];
  };
};

// Actions
const createAsync = geneAsyncAction(featureName);
const api = {
  tax: {
    get: createAsync<undefined, ISettingTaxData>('getTax'),
  },
  discount: {
    get: createAsync<undefined, ICashDiscountIncentiveData>('getDiscount'),
  },
  feeCreditCard: {
    get: createAsync<undefined, IFeeCreditCardData>('getFeeCreditCard'),
  },
  loyalty: {
    get: createAsync<undefined, ILoyaltyProgramItem[]>('getLoyalty'),
  },
  promotion: {
    get: {
      list: createAsync<IGetPromotionsParams, IPromotionList>('getPromotions'),
      detail: createAsync<string, IPromotionDetail>('getPromotionDetail'),
    },
  },
  referral: {
    get: createAsync('getReferral'),
    update: createAsync<IUpdateReferralConfig[]>('updateReferral'),
  },
};

// Selectors
const getState = (state: RootState) => state['settingGeneral'];
const select = (key: keyof SliceState) =>
  useAppSelector((state) => get(getState(state), key));

export const use = {
  tax: () => select('tax'),
  discount: () => select('discount'),
  feeCreditCard: () => select('feeCreditCard'),
  loyalty: () => select('loyalty'),
  promotionList: () => select('promotionList'),
  promotionDetail: () => select('promotionDetail'),
  referral: () => select('referral'),
};

// Slice
const slice = createSlice({
  name: featureName,
  initialState: {
    tax: { status: undefined },
    discount: { status: undefined },
    loyalty: { status: undefined },
    feeCreditCard: { status: undefined },
    promotionList: { status: undefined },
    promotionDetail: { status: undefined },
    referral: { status: undefined },
  } as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    // Tax Settings
    builder
      .addCase(api.tax.get.fetch, (state) => {
        state.tax.status = 'fetching';
        state.tax.error = undefined;
      })
      .addCase(api.tax.get.success, (state, { payload }) => {
        state.tax.status = 'success';
        state.tax.data = payload;
      })
      .addCase(api.tax.get.fail, (state, { payload }) => {
        state.tax.status = 'error';
        state.tax.error = payload;
      });

    // Discount Settings
    builder
      .addCase(api.discount.get.fetch, (state) => {
        state.discount.status = 'fetching';
        state.discount.error = undefined;
      })
      .addCase(api.discount.get.success, (state, { payload }) => {
        state.discount.status = 'success';
        state.discount.data = payload;
      })
      .addCase(api.discount.get.fail, (state, { payload }) => {
        state.discount.status = 'error';
        state.discount.error = payload;
      });

    // Fee Credit Card Settings
    builder
      .addCase(api.feeCreditCard.get.fetch, (state) => {
        state.feeCreditCard.status = 'fetching';
        state.feeCreditCard.error = undefined;
      })
      .addCase(api.feeCreditCard.get.success, (state, { payload }) => {
        state.feeCreditCard.status = 'success';
        state.feeCreditCard.data = payload;
      })
      .addCase(api.feeCreditCard.get.fail, (state, { payload }) => {
        state.feeCreditCard.status = 'error';
        state.feeCreditCard.error = payload;
      });

    // Loyalty Program
    builder
      .addCase(api.loyalty.get.fetch, (state) => {
        state.loyalty.status = 'fetching';
        state.loyalty.error = undefined;
      })
      .addCase(api.loyalty.get.success, (state, { payload }) => {
        state.loyalty.status = 'success';
        state.loyalty.data = payload;
      })
      .addCase(api.loyalty.get.fail, (state, { payload }) => {
        state.loyalty.status = 'error';
        state.loyalty.error = payload;
      });

    // Promotion List
    builder
      .addCase(api.promotion.get.list.fetch, (state) => {
        state.promotionList.status = 'fetching';
        state.promotionList.error = undefined;
      })
      .addCase(api.promotion.get.list.success, (state, { payload }) => {
        state.promotionList.status = 'success';
        state.promotionList.data = payload;
      })
      .addCase(api.promotion.get.list.fail, (state, { payload }) => {
        state.promotionList.status = 'error';
        state.promotionList.error = payload;
      });

    // Promotion Detail
    builder
      .addCase(api.promotion.get.detail.fetch, (state) => {
        state.promotionDetail.status = 'fetching';
        state.promotionDetail.error = undefined;
      })
      .addCase(api.promotion.get.detail.success, (state, { payload }) => {
        state.promotionDetail.status = 'success';
        state.promotionDetail.data = payload;
      })
      .addCase(api.promotion.get.detail.fail, (state, { payload }) => {
        state.promotionDetail.status = 'error';
        state.promotionDetail.error = payload;
      });
    builder
      .addCase(api.referral.get.fetch, (state) => {
        state.referral.status = 'fetching';
        state.referral.error = undefined;
      })
      .addCase(api.referral.get.success, (state, { payload }) => {
        state.referral.status = 'success';
        console.log('data', payload);
        state.referral.data = payload;
      })
      .addCase(api.referral.get.fail, (state, { payload }) => {
        state.referral.status = 'error';
        state.referral.error = payload;
      })
      .addCase(api.referral.update.fetch, (state) => {
        state.referral.status = 'fetching';
        state.referral.error = undefined;
      })
      .addCase(api.referral.update.success, (state, { payload }) => {
        state.referral.status = 'success';
        state.referral.data = payload;
      })
      .addCase(api.referral.update.fail, (state, { payload }) => {
        state.referral.status = 'error';
        state.referral.error = payload;
      });
  },
});

export const actions = { ...slice.actions, api };
export const reducer = slice.reducer;
